/* screens/gift/GiftRedeem.jsx — активация сертификата (/gift/:code и /gift/redeem).

   Сюда приходят ПОСТОРОННИЕ люди: получатель почти никогда не пользователь
   продукта. Поэтому экран прошит ВНЕ RequireAuth — гард увёл бы на /signin и
   потерял код из адреса, а человек, не понявший, куда попал, просто закрыл бы
   вкладку. Вход инициируем сами, сохранив код в sessionStorage: SignIn и SignUp
   читают тот же ключ и возвращают человека обратно. Приём повторяет именные
   ссылки на оплату (PayByLink) — там он уже отработан.

   Главное решение экрана — показать ПОСЛЕДСТВИЯ до нажатия. Если у человека
   уже идёт платный тариф, а подарок на другой, часть оплаченных дней сгорит.
   Сказать об этом после активации нельзя: она необратима. Расчёт делает сервер
   (`describe_apply`) и отдаёт в превью — повторять формулу здесь запрещено,
   иначе предупреждение и реальное списание однажды разойдутся. */

import React from 'react';
import { useAuth } from '../../app/providers/AuthProvider.jsx';
import { CheckoutLayout } from '../checkout/CheckoutLayout.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Field, TextInput } from '../../components/ui/Field.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { ScreenHeader } from '../../components/ui/Metric.jsx';
import { Spinner } from '../../components/ui/Spinner.jsx';
import * as giftsApi from '../../lib/api/gifts.js';
import { fmt } from '../../lib/format.js';
import { RR } from '../../lib/router.jsx';

const { useState, useEffect, useCallback } = React;
const { useNavigate, useParams } = RR;

// Свой ключ возврата, отдельный от приглашений и именных ссылок: сценарии
// независимы, и подарок не должен перехватывать чужой возврат.
const PENDING_GIFT_KEY = 'pending_gift_code';

function periodLabel(days) {
  if (days % 30 === 0 && days >= 30) {
    const m = days / 30;
    return `${m} ${fmt.plural(m, 'месяц', 'месяца', 'месяцев')}`;
  }
  return `${days} ${fmt.plural(days, 'день', 'дня', 'дней')}`;
}

function humanDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  // Без хвостового « г.»: ru-RU добавляет его сам, а дата стоит в середине
  // предложения, за которым идёт точка — получалось «26 сентября 2026 г..».
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })
    .replace(/\s*г\.$/, '');
}

/** Что именно случится с подпиской — словами, а не режимом из API. */
function EffectNote({ effect }) {
  if (!effect) return null;
  if (effect.mode === 'extend') {
    return (
      <div className="callout callout--good">
        <Icon name="check" size={16} />
        <span>Срок текущего тарифа продлится до {humanDate(effect.new_expires_at)}.</span>
      </div>
    );
  }
  if (effect.mode === 'replace') {
    return (
      <div className="callout callout--warn" role="alert">
        <Icon name="alert-circle" size={16} />
        <span>
          Сейчас у вас другой платный тариф. После активации он сменится на
          подарочный до {humanDate(effect.new_expires_at)}, а оставшиеся{' '}
          {effect.lost_days} {fmt.plural(effect.lost_days, 'день', 'дня', 'дней')} текущего
          сгорят. Если хотите использовать их полностью — активируйте позже.
        </span>
      </div>
    );
  }
  return (
    <div className="callout callout--info">
      <Icon name="info" size={16} />
      <span>Тариф включится сразу и будет действовать до {humanDate(effect.new_expires_at)}.</span>
    </div>
  );
}

function GiftRedeem() {
  const { code: codeFromUrl } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [code, setCode] = useState(codeFromUrl || '');
  const [preview, setPreview] = useState(null);
  const [phase, setPhase] = useState(codeFromUrl ? 'loading' : 'input');
  // input | loading | ready | error | done
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  // B2B: куда включить тариф.
  const [target, setTarget] = useState('new');
  const [wsId, setWsId] = useState(null);
  const [wsName, setWsName] = useState('');
  const [result, setResult] = useState(null);

  const stash = useCallback((value) => {
    try { sessionStorage.setItem(PENDING_GIFT_KEY, value); } catch { /* приватный режим */ }
  }, []);

  const load = useCallback(async (raw) => {
    const value = (raw || '').trim();
    if (!value) return;
    setPhase('loading');
    setError(null);
    const r = await giftsApi.previewGift(value);
    if (!r.ok) {
      setError((r.data && r.data.detail) || 'Не удалось проверить код.');
      setPhase('error');
      return;
    }
    setPreview(r.data);
    if (r.data.audience === 'business') {
      const list = r.data.workspaces || [];
      // Есть ровно одно своё заведение — предлагаем его: создавать второе
      // пустое пространство владельцу одного ресторана незачем.
      if (list.length === 1) { setTarget('existing'); setWsId(list[0].id); }
      else setTarget(list.length ? 'existing' : 'new');
      if (list.length) setWsId((cur) => cur || list[0].id);
    }
    setPhase('ready');
  }, []);

  useEffect(() => {
    if (!codeFromUrl) return;
    if (!user) { stash(codeFromUrl); return; }
    load(codeFromUrl);
  }, [codeFromUrl, user, load, stash]);

  // Пришёл по ссылке без входа — предлагаем два пути, как и письмо.
  if (codeFromUrl && !user) {
    return (
      <CheckoutLayout>
        <div className="stack stack-16 gift-redeem">
          <ScreenHeader title="Вам подарили Ресториум"
            subtitle="Чтобы активировать сертификат, войдите или создайте аккаунт" />
          <div className="gift-redeem__code num">{codeFromUrl}</div>
          <div className="stack stack-8">
            <Button variant="primary" size="lg" className="full"
              onClick={() => { stash(codeFromUrl); navigate('/signup?gift=' + encodeURIComponent(codeFromUrl)); }}>
              Создать аккаунт
            </Button>
            <Button variant="secondary" size="lg" className="full"
              onClick={() => { stash(codeFromUrl); navigate('/signin'); }}>
              У меня уже есть аккаунт
            </Button>
          </div>
          <p className="t-caption text-mute">
            Код сохранится — после входа останется нажать «Активировать».
          </p>
        </div>
      </CheckoutLayout>
    );
  }

  const activate = async () => {
    setBusy(true);
    setError(null);
    const body = { code: preview ? preview.code : code };
    if (preview && preview.audience === 'business') {
      body.target = target;
      if (target === 'existing') body.workspace_id = wsId;
      else body.new_workspace_name = wsName.trim();
    }
    const r = await giftsApi.redeemGift(body);
    setBusy(false);
    if (!r.ok) {
      setError((r.data && r.data.detail) || 'Не удалось активировать сертификат.');
      return;
    }
    try { sessionStorage.removeItem(PENDING_GIFT_KEY); } catch { /* noop */ }
    setResult(r.data);
    setPhase('done');
  };

  const isBusiness = preview && preview.audience === 'business';
  const workspaces = (preview && preview.workspaces) || [];
  const effect = preview
    ? (isBusiness
        ? (preview.effects || {})[String(wsId)]
        : preview.effect)
    : null;
  const canActivate = !isBusiness
    || (target === 'existing' ? !!wsId : !!wsName.trim());

  return (
    <CheckoutLayout>
      <div className="gift-redeem stack stack-16">
        <ScreenHeader title="Подарочный сертификат"
          subtitle="Введите код — покажем, что он даёт, до активации" />

      {phase === 'done' && result && (
        <section className="stack stack-16">
          <div className="gift-done">
            <Icon name="check" size={30} className="gift-done__icon" />
            <h2 className="t-h3">Готово — тариф включён</h2>
            <p className="t-body text-dim">
              «{result.plan_name}» активен в пространстве «{result.workspace.name}»
              {result.expires_at ? ` до ${humanDate(result.expires_at)}` : ''}.
            </p>
          </div>
          <div className="callout callout--info">
            <Icon name="info" size={16} />
            <span>
              Автосписаний по подарку нет. Ближе к концу срока мы напомним,
              что подписку нужно оформить самому, — работа не прервётся внезапно.
            </span>
          </div>
          <div className="gift-actions">
            {result.workspace.kind === 'business' ? (
              <Button variant="primary" size="lg"
                onClick={() => navigate('/settings?tab=business')}>
                Настроить заведение
              </Button>
            ) : (
              <Button variant="primary" size="lg" onClick={() => navigate('/chat')}>
                Перейти в ассистента
              </Button>
            )}
          </div>
        </section>
      )}

      {phase !== 'done' && (
        <section className="stack stack-16">
          {error && (
            <div className="callout callout--bad" role="alert">
              <Icon name="alert-circle" size={16} />
              <div className="stack stack-4">
                <span>{error}</span>
                <span className="t-caption text-mute">
                  Проверьте код — его легко перепутать при наборе. Если он верный,
                  напишите в поддержку, мы разберёмся.
                </span>
              </div>
            </div>
          )}

          {(phase === 'input' || phase === 'error') && (
            <div className="stack stack-12">
              <Field label="Код сертификата" htmlFor="gr-code"
                hint="Он напечатан на сертификате и есть в письме">
                <TextInput id="gr-code" value={code} placeholder="XXXX-XXXX-XXXX"
                  autoComplete="off" spellCheck={false}
                  onChange={(e) => setCode(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') load(code); }} />
              </Field>
              <Button variant="primary" size="lg" disabled={!code.trim()}
                onClick={() => load(code)}>Проверить код</Button>
            </div>
          )}

          {phase === 'loading' && (
            <div className="gift-loading"><Spinner /> <span>Проверяем код…</span></div>
          )}

          {phase === 'ready' && preview && (
            <div className="stack stack-16">
              <div className="gift-prize">
                <div className="gift-prize__name">{preview.plan.name}</div>
                <div className="gift-prize__period t-body text-dim">
                  на {periodLabel(preview.period_days)}
                </div>
                {preview.sender_name && (
                  <div className="t-caption text-mute">от {preview.sender_name}</div>
                )}
              </div>

              {preview.message && (
                <blockquote className="gift-message">{preview.message}</blockquote>
              )}

              {isBusiness && (
                <div className="stack stack-8">
                  <h3 className="t-h4">Куда включить тариф</h3>
                  {workspaces.length > 0 && (
                    <label className="gift-radio">
                      <input type="radio" name="gift-target" checked={target === 'existing'}
                        onChange={() => setTarget('existing')} />
                      <span>В существующее заведение</span>
                    </label>
                  )}
                  {target === 'existing' && workspaces.length > 0 && (
                    <div className="gift-ws-list">
                      {workspaces.map((w) => (
                        <label key={w.id} className={'gift-ws' + (wsId === w.id ? ' is-selected' : '')}>
                          <input type="radio" name="gift-ws" checked={wsId === w.id}
                            onChange={() => setWsId(w.id)} />
                          <span className="gift-ws__name">{w.name}</span>
                          {w.plan_name && (
                            <span className="t-caption text-mute">сейчас: {w.plan_name}</span>
                          )}
                        </label>
                      ))}
                    </div>
                  )}
                  <label className="gift-radio">
                    <input type="radio" name="gift-target" checked={target === 'new'}
                      onChange={() => setTarget('new')} />
                    <span>Создать новое заведение</span>
                  </label>
                  {target === 'new' && (
                    <Field label="Название заведения" htmlFor="gr-ws"
                      hint="Так оно будет называться в вашем списке">
                      <TextInput id="gr-ws" value={wsName} placeholder="Кафе на Садовой"
                        onChange={(e) => setWsName(e.target.value)} />
                    </Field>
                  )}
                </div>
              )}

              <EffectNote effect={effect} />

              <div className="gift-actions">
                <Button variant="primary" size="lg" loading={busy} disabled={!canActivate}
                  onClick={activate}>Активировать</Button>
                <Button variant="ghost" size="lg"
                  onClick={() => { setPreview(null); setPhase('input'); }}>
                  Другой код
                </Button>
              </div>
            </div>
          )}
        </section>
        )}
      </div>
    </CheckoutLayout>
  );
}

export { GiftRedeem, PENDING_GIFT_KEY };
